import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { cores } from '../../../styles/styleUtils';

const moeda = (valor) =>
  (Number(valor) || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const dataInput = (valor) => {
  if (!valor) return '';
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return '';
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

export default function ParcelasFuturasDialog({
  open,
  onClose,
  registro,
  tipo,
  previewFn,
  executeFn,
  onSuccess,
  permiteCancelar = true,
  permiteValor = true,
}) {
  const [acao, setAcao] = useState('editar');
  const [preview, setPreview] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [novaData, setNovaData] = useState('');
  const [novoValor, setNovoValor] = useState('');
  const [motivo, setMotivo] = useState('');

  useEffect(() => {
    if (!open || !registro?._id) return;

    setAcao('editar');
    setPreview(null);
    setErro('');
    setNovaData(dataInput(registro.vencimento));
    setNovoValor('');
    setMotivo('');

    const carregar = async () => {
      setCarregando(true);
      const result = await previewFn({
        acao: 'editar',
        preview: true,
        ...(tipo === 'receita'
          ? { receitaId: registro._id }
          : { despesaId: registro._id }),
      });
      setCarregando(false);

      if (result?.success === false || result?.ok === false) {
        setErro(result?.message || result?.msg || 'Não foi possível consultar o grupo.');
        return;
      }

      setPreview(result);
    };

    carregar();
  }, [open, registro, tipo, previewFn]);

  const confirmar = async () => {
    setErro('');

    if (acao === 'cancelar' && motivo.trim().length < 3) {
      setErro('Informe o motivo do cancelamento.');
      return;
    }

    if (acao === 'editar' && !novaData && !novoValor) {
      setErro('Informe uma nova data e/ou um novo valor.');
      return;
    }

    const resumoAcao =
      acao === 'cancelar'
        ? `${preview?.quantidade || 0} parcela(s)/ocorrência(s) futura(s) serão cancelada(s).`
        : `${preview?.quantidade || 0} parcela(s)/ocorrência(s) futura(s) serão alterada(s).`;

    if (!window.confirm(`${resumoAcao}\n\nDeseja continuar?`)) return;

    setCarregando(true);
    const payload = {
      acao,
      preview: false,
      ...(tipo === 'receita'
        ? { receitaId: registro._id }
        : { despesaId: registro._id }),
      ...(acao === 'editar'
        ? {
            novaDataPrimeiroVencimento: novaData || undefined,
            novoValor:
              permiteValor && novoValor !== ''
                ? Number(novoValor)
                : undefined,
          }
        : { motivoCancelamento: motivo.trim() }),
    };

    const result = await executeFn(payload);
    setCarregando(false);

    if (result?.success === false || result?.ok === false) {
      setErro(result?.message || result?.msg || 'Não foi possível concluir a ação.');
      return;
    }

    window.alert(result?.message || 'Ação concluída com sucesso.');
    onSuccess?.();
    onClose();
  };

  return (
    <Dialog open={open} onClose={carregando ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Parcelas futuras</DialogTitle>
      <DialogContent>
        {erro && <Alert severity="warning" sx={{ mb: 2 }}>{erro}</Alert>}

        {preview && (
          <Box sx={{ mb: 2 }}>
            <Typography><strong>Quantidade afetada:</strong> {preview.quantidade}</Typography>
            <Typography><strong>Total atual:</strong> {moeda(preview.totalAtual)}</Typography>
            <Typography>
              <strong>Faixa:</strong> parcela {preview.primeiraParcela || '—'} até {preview.ultimaParcela || '—'}
            </Typography>
            {preview.bloqueadasParciais > 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {preview.bloqueadasParciais} parcela(s) com baixa parcial foram protegidas e não serão alteradas.
              </Alert>
            )}
            {preview.ligadaVenda && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Esta receita pertence a uma venda. Para preservar a conciliação, somente o reagendamento de vencimentos é permitido.
              </Alert>
            )}
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        <FormControl>
          <RadioGroup row value={acao} onChange={(e) => setAcao(e.target.value)}>
            <FormControlLabel value="editar" control={<Radio />} label="Alterar futuras" />
            {permiteCancelar && (
              <FormControlLabel value="cancelar" control={<Radio />} label="Cancelar futuras" />
            )}
          </RadioGroup>
        </FormControl>

        {acao === 'editar' ? (
          <Box sx={{ display: 'grid', gap: 2, mt: 2 }}>
            <TextField
              type="date"
              label="Novo vencimento da primeira parcela afetada"
              value={novaData}
              onChange={(e) => setNovaData(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="As demais datas serão deslocadas pelo mesmo intervalo, preservando o espaçamento atual."
            />
            {permiteValor && (
              <TextField
                type="number"
                label="Novo valor por parcela/ocorrência (opcional)"
                value={novoValor}
                onChange={(e) => setNovoValor(e.target.value)}
                inputProps={{ min: 0.01, step: 0.01 }}
              />
            )}
          </Box>
        ) : (
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Motivo do cancelamento"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={carregando}>Voltar</Button>
        <Button
          onClick={confirmar}
          disabled={carregando || !preview}
          variant="contained"
          sx={{
            backgroundColor: cores.laranja,
            '&:hover': { backgroundColor: cores.laranjaEscuro },
          }}
        >
          {carregando ? 'Processando...' : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
